import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { practiceService } from '../api/services/practice';
import { PracticeSession } from '../components/Practice/PracticeSession';
import { PracticeSummary } from '../components/Practice/PracticeSummary';
import { PracticeSubtopicSelect } from '../components/Practice/PracticeSubtopicSelect';
import { PracticeDurationSelect } from '../components/Practice/PracticeDurationSelect';
import { PracticeNoContent } from '../components/Practice/PracticeNoContent';
import { APIResponse } from '@memriti/shared';
import Container from '../components/common/Container';

export const Practice: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<APIResponse<any> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noContentAvailable, setNoContentAvailable] = useState(false);
  const [selectedSubtopics, setSelectedSubtopics] = useState<number[] | undefined>(undefined);
  const [selectedCompanyTags, setSelectedCompanyTags] = useState<number[] | undefined>(undefined);
  const [hasCompletedSelection, setHasCompletedSelection] = useState(false);
  const hasInitialized = useRef(false);
  const isInitializing = useRef(false);

  useEffect(() => {
    const initializeSession = async () => {
      if (isInitializing.current || hasInitialized.current) return;

      try {
        isInitializing.current = true;
        if (!user) {
          navigate('/login');
          return;
        }

        // If we have a session in state, use it
        if (location.state?.session) {
          console.log('Using session from state:', location.state.session);
          setSession(location.state.session);
          hasInitialized.current = true;
          return;
        }

        // Otherwise, show the subtopic selection
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start practice session');
      } finally {
        isInitializing.current = false;
      }
    };

    initializeSession();
  }, [user, navigate, location.state]);

  const handleSubtopicsSelected = (subtopicIds: number[] | undefined, companyTagIds: number[] | undefined) => {
    setSelectedSubtopics(subtopicIds);
    setSelectedCompanyTags(companyTagIds);
    setHasCompletedSelection(true);
  };

  const handleBackToSubtopics = () => {
    setSelectedSubtopics(undefined);
    setSelectedCompanyTags(undefined);
    setHasCompletedSelection(false);
    setNoContentAvailable(false);
    setError(null);
  };

  const handleBackFromNoContent = () => {
    setNoContentAvailable(false);
    setHasCompletedSelection(false);
  };

  const handleDurationSelected = async (duration_minutes: number) => {
    try {
      setIsLoading(true);
      setError(null);
      setNoContentAvailable(false);
      console.log('Starting session with duration:', duration_minutes, 'subtopics:', selectedSubtopics, 'company tags:', selectedCompanyTags);
      const response = await practiceService.startSession(
        duration_minutes,
        selectedSubtopics,
        selectedCompanyTags
      );
      console.log('Session started:', response);
      setSession(response);
      setIsLoading(false);
    } catch (err: any) {
      console.log('Error starting session:', err);
      console.log('Error response:', err.response?.data);

      let errorMessage = 'Failed to start practice session';

      // Try to get error message from different possible sources
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      // Check if this is the "no content available" error
      const noContentKeywords = [
        'No content items available',
        'No content items available for practice',
        'no content available',
        'no practice content',
        'no items available'
      ];

      const isNoContentError = noContentKeywords.some(keyword =>
        errorMessage.toLowerCase().includes(keyword.toLowerCase())
      );

      if (isNoContentError) {
        setNoContentAvailable(true);
      } else {
        setError(errorMessage);
      }
      setIsLoading(false);
    }
  };

  const handleSessionComplete = async (sessionId: number) => {
    try {
      const response = await practiceService.completeSession(sessionId);
      setSession(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete practice session');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-red-500 text-center">{error}</div>
      </div>
    );
  }

  if (!session) {
    return (
      <Container className="px-4 py-6">
        {noContentAvailable ? (
          <PracticeNoContent
            onBack={handleBackFromNoContent}
            selectedSubtopics={selectedSubtopics}
            selectedCompanyTags={selectedCompanyTags}
          />
        ) : !hasCompletedSelection ? (
          <PracticeSubtopicSelect onSubtopicsSelected={handleSubtopicsSelected} />
        ) : (
          <PracticeDurationSelect
            onDurationSelected={handleDurationSelected}
            onBack={handleBackToSubtopics}
          />
        )}
      </Container>
    );
  }

  return (
    <Container className="px-4 py-6">
      {session.data.status === 'completed' ? (
        <PracticeSummary session={session} />
      ) : (
        <PracticeSession
          session={session}
          onComplete={handleSessionComplete}
        />
      )}
    </Container>
  );
};